<script lang="ts">
  import Icon from '@iconify/svelte'
  import { writable } from 'svelte/store'
  import { z, ZodError } from 'zod/v4'

  import {
    PUBLIC_EMAILJS_KEY,
    PUBLIC_EMAILJS_SERVICE_ID,
    PUBLIC_EMAILJS_TEMPLATE_ID,
  } from '$env/static/public'

  import emailjs from '@emailjs/browser'

  import Button from '~/components/ui/button.svelte'
  import Container from '~/components/ui/container.svelte'
  import LinkButton from '~/components/ui/link-button.svelte'

  let schema = z.object({
    ct_name: z.string().min(1, 'Campo obrigatório'),
    ct_phone: z.string().min(1, 'Campo obrigatório'),
    ct_email: z.string().min(1, 'Campo obrigatório'),
    ct_service: z.string().min(1, 'Campo obrigatório'),
    ct_message: z.string().min(1, 'Campo obrigatório'),
  })

  let form = writable<{ data: z.infer<typeof schema>; errors: Partial<z.infer<typeof schema>> }>({
    data: { ct_name: '', ct_email: '', ct_message: '', ct_phone: '', ct_service: '' },
    errors: { ct_name: '', ct_email: '', ct_message: '', ct_phone: '', ct_service: '' },
  })

  let status = $state<'idle' | 'loading' | 'error' | 'success'>('idle')

  const validate = (field?: keyof typeof $form.data) => {
    let e = {} as Partial<Record<keyof typeof $form.data, string>>

    if (!field) {
      $form.errors = e

      return schema.safeParse($form.data).success
    }

    if (schema.shape[field] || !field) {
      try {
        schema.shape[field].parse($form.data[field])
      } catch (err: unknown) {
        if (err instanceof Error) {
          e[field] = (err as ZodError<z.infer<typeof schema>>).flatten().formErrors[0]
        }
      }
    }

    $form.errors = { ...$form.errors, [field]: e[field] }

    if (!e[field]) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [field]: _, ...rest } = $form.errors
      $form.errors = rest as typeof $form.errors
    }
  }

  const submit = (e: SubmitEvent) => {
    e.preventDefault()
    e.stopPropagation()

    status = 'loading'

    if (validate()) {
      const service = (() => {
        let mapper = {
          // sass: 'Desenvolvimento de Saas (Software as a Service)',
          landing: 'Landing Page',
          software: 'Desenvolvimento de Software / Desenvolvimento Web',
          ecommerce: 'Loja virtual / Loja Online',
          integrations: 'Integrações e Automação',
          institucional: 'Site Institucional',
        }

        return mapper[$form.data.ct_service as keyof typeof mapper]
      })()

      // prettier-ignore
      emailjs
				.send(
					PUBLIC_EMAILJS_SERVICE_ID,
					PUBLIC_EMAILJS_TEMPLATE_ID,
					{ time: new Date().toString(), name: $form.data.ct_name, title: `Cliente ${$form.data.ct_name} pediu um orçamento de ${service}`, phone: $form.data.ct_phone, email: $form.data.ct_email, service: service, message: $form.data.ct_message },
					PUBLIC_EMAILJS_KEY
				)
				.then(() => (status = 'loading'))
				.catch(() => (status = 'error'))
				.finally(() => (status = 'idle'));
    }
  }

  // prettier-ignore
  const lists = [
		{ icon: 'fa6-solid:phone', anchorName: '+55 (11) 99310-8947', anchorTo: 'tel:+5511993108947' },
		{ icon: 'fa6-brands:whatsapp', anchorName: '+55 (11) 99310-8947', anchorTo: 'https://wa.me/5511993108947?text=Ol%C3%A1%20gostaria%20de%20ver%20um%20or%C3%A7amento%20sem%20compromisso.' },
		{ icon: 'fa6-solid:envelope', anchorName: 'contact@lutruvials.com', anchorTo: 'mailto:contact@lutruvials.com' }
	];
</script>

<style lang="postcss">
  .form-control label span {
    display: block;
    margin-bottom: 8px;
  }

  .form-control label span span {
    @apply text-red-500;
  }

  .form-control label input {
    width: 100%;
    height: 48px;
  }

  .form-control label textarea {
    width: 100%;
    min-height: 180px;

    resize: vertical;
  }
</style>

<section>
  <Container>
    <div class="flex flex-col lg:pb-[96px]">
      <div class="w-full flex-grow">
        <h2
          class="title | mb-3 text-start text-4xl font-medium text-black md:text-5xl lg:w-3/5 lg:leading-snug lg:text-black"
        >
          Orçamento com preço fixo e escopo fechado para seu projeto.
        </h2>
        <p class="mb-4 text-start text-sm text-black md:text-lg lg:w-3/4 lg:text-black">
          Conte-nos sobre o seu projeto de software, aplicativo, sistema web ou site. Nossa equipe
          analisa e retorna com uma proposta técnica e comercial personalizada, geralmente em até 24
          horas. <span class="sr-only">
            Se preferir, você também pode agendar uma conversa diretamente com um de nossos
            especialistas.
          </span>
        </p>
      </div>

      <div class="flex-grow items-start rounded-2xl lg:flex-row lg:bg-black lg:p-3">
        <div class="flex flex-col rounded-lg md:bg-white lg:flex-row">
          <div class="lg:p-8">
            <ul class="mb-4 space-y-6 md:space-y-3">
              {#each lists as childItem}
                <li class="">
                  <LinkButton
                    size="none"
                    colorPalette="black"
                    variant="plain"
                    class="flex items-center gap-2 whitespace-nowrap text-sm"
                    href={childItem.anchorTo}
                    target="_blank"
                  >
                    <Icon class="size-[18px] text-[18px] leading-[18px]" icon={childItem.icon} />

                    {childItem.anchorName}
                  </LinkButton>
                </li>
              {/each}

              <li>
                <address class="w-4/5 font-bold not-italic">
                  08595-360, Parque Macedo - Itaquaquecetuba - São Paulo
                </address>
              </li>
            </ul>
          </div>

          <div class="w-full flex-grow lg:p-8">
            <form
              action=""
              class="flex h-full flex-col items-start gap-3 lg:flex-row lg:gap-8"
              onsubmit={submit}
            >
              <div class="w-full flex-grow space-y-3 lg:w-1/2">
                <div class="form-control">
                  <label for="name">
                    <span class="!flex items-center gap-1 font-medium">
                      Nome Completo
                      <span class="!m-0 size-[4px] !leading-[4px] text-red-500">*</span>
                    </span>
                    <input
                      id="name"
                      type="text"
                      name="name"
                      class="rounded-lg"
                      placeholder="Como podemos te chamar?"
                      oninput={() => validate('ct_name')}
                      bind:value={$form.data.ct_name}
                    />

                    {#if $form.errors.ct_name}
                      <span class="text-red-500">{$form.errors.ct_name}</span>
                    {/if}
                  </label>
                </div>
                <div class="form-control">
                  <label for="email">
                    <span class="!flex items-center gap-1 font-medium">
                      E-mail
                      <span class="!m-0 size-[4px] !leading-[4px] text-red-500">*</span>
                    </span>
                    <input
                      id="email"
                      type="text"
                      name="email"
                      class="rounded-lg"
                      placeholder="Qual é seu melhor e-mail?"
                      oninput={() => validate('ct_email')}
                      bind:value={$form.data.ct_email}
                    />

                    {#if $form.errors.ct_email}
                      <span class="text-red-500">{$form.errors.ct_email}</span>
                    {/if}
                  </label>
                </div>
                <div class="form-control">
                  <label for="phone">
                    <span class="!flex items-center gap-1 font-medium">
                      Telefone/WhatsApp
                      <span class="!m-0 size-[4px] !leading-[4px] text-red-500">*</span>
                    </span>
                    <input
                      id="phone"
                      type="text"
                      name="phone"
                      class="rounded-lg"
                      placeholder="Qual número podemos ligar?"
                      oninput={() => validate('ct_phone')}
                      bind:value={$form.data.ct_phone}
                    />

                    {#if $form.errors.ct_phone}
                      <span class="text-red-500">{$form.errors.ct_phone}</span>
                    {/if}
                  </label>
                </div>
              </div>
              <div class="hidden h-[280px] w-1 rounded-md bg-black lg:block"></div>
              <div class="w-full flex-grow space-y-3 lg:w-2/3">
                <div class="form-control">
                  <label for="service">
                    <span class="!flex items-center gap-1 font-medium">
                      Serviço de interesse:
                      <span class="!m-0 size-[4px] !leading-[4px] text-red-500">*</span>
                    </span>

                    <select
                      id="service"
                      name="service"
                      placeholder="Selecione o serviço"
                      class="w-full rounded-lg"
                      oninput={() => validate('ct_service')}
                      bind:value={$form.data.ct_service}
                    >
                      <option value="" selected hidden>Selecione o serviço</option>
                      <option value="landing">Landing Page / Site One Page</option>
                      <option value="institucional">Site Institucional</option>
                      <option value="ecommerce">Lojas Virtuais / Lojas Online</option>
                      <!-- <option value="saas">Desenvolvimento de Saas(Software as a Service)</option> -->
                      <option value="software">
                        Desenvolvimento de Software / Desenvolvimento Web
                      </option>
                      <option value="integrations">Integrações e Automação</option>
                    </select>

                    {#if $form.errors.ct_service}
                      <span class="text-red-500">{$form.errors.ct_service}</span>
                    {/if}
                  </label>
                </div>
                <div class="form-control">
                  <label for="message">
                    <span class="!flex items-center gap-1 font-medium">
                      Descreva sobre o seu projeto:
                      <span class="!m-0 size-[4px] !leading-[4px] text-red-500">*</span>
                    </span>
                    <textarea
                      id="message"
                      name="message"
                      class="rounded-lg"
                      placeholder="Conte um pouco sobre os objetivos, funcionalidades e desafios do sua empresa."
                      oninput={() => validate('ct_message')}
                      bind:value={$form.data.ct_message}
                    ></textarea>
                    {#if $form.errors.ct_message}
                      <span class="text-red-500">{$form.errors.ct_message}</span>
                    {/if}
                  </label>
                </div>

                <Button
                  variant="solid"
                  type="submit"
                  colorPalette="brand"
                  class="ml-auto flex w-3/12 items-center"
                >
                  Enviar
                </Button>

                {#if status === 'loading'}
                  <p>Carregando...</p>
                {:else if status === 'success'}
                  <p>Enviado com sucesso!</p>
                {:else if status === 'error'}
                  <p>Ocorreu um problema, por favor tente mais tarde.</p>
                {:else}
                  <p></p>
                {/if}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </Container>
</section>
